import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Image,
  Video,
  Camera,
  Upload,
  Sparkles,
  FileSearch,
  Trash2,
  Download,
  Copy,
  FolderOpen,
  X,
  Maximize2,
  Heart,
  CalendarIcon,
  CheckSquare,
  Square,
  Loader2,
} from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { api } from '@/lib/api'
import { getAssetThumbnailUrl } from '@/lib/assets'
import { downloadFile, downloadFilesAsZip } from '@/lib/download'
import { formatDate } from '@/lib/utils'
import { getDateFilters, TIME_RANGE_OPTIONS, type TimeRange } from '@/lib/date-filters'
import { getAssetUploadAccept, validateAssetFiles } from '@/lib/assetUpload'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FileUploadZone } from '@/components/library/FileUploadZone'
import { UploadProgressList, type UploadItem } from '@/components/library/UploadProgressList'
import type { Asset, AssetDetail, SourceType, AssetType, GenerationDetails } from '@/types'
import { embedRequirementId } from '@/lib/embedContext'

const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; icon: typeof Camera; color: string }> = {
  screenshot: { label: 'Screenshot', icon: Camera, color: 'bg-blue-100 text-blue-700' },
  uploaded: { label: 'Uploaded', icon: Upload, color: 'bg-green-100 text-green-700' },
  generated: { label: 'Generated', icon: Sparkles, color: 'bg-purple-100 text-purple-700' },
  reference: { label: 'Reference', icon: FileSearch, color: 'bg-orange-100 text-orange-700' },
  feedback_reference: { label: 'FB Ref', icon: FileSearch, color: 'bg-gray-100 text-gray-700' },
  feedback_variant: { label: 'FB Variant', icon: FileSearch, color: 'bg-gray-100 text-gray-700' },
}

// Source types shown in filters and stats cards
const VISIBLE_SOURCE_TYPES: SourceType[] = ['screenshot', 'uploaded', 'generated', 'reference', 'feedback_reference', 'feedback_variant']

const ASSET_TYPE_CONFIG: Record<AssetType, { label: string; icon: typeof Image }> = {
  image: { label: 'Image', icon: Image },
  video: { label: 'Video', icon: Video },
}

export function LibraryPage() {
  const queryClient = useQueryClient()
  const uploadInputRef = useRef<HTMLInputElement>(null)

  // Filter state
  const [productId, setProductId] = useState<string>('')
  const [sourceType, setSourceType] = useState<SourceType | ''>('')
  const [assetType, setAssetType] = useState<AssetType | ''>('')
  const [isFavorited, setIsFavorited] = useState<boolean | null>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>('')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const [offset, setOffset] = useState(0)
  const limit = 40

  // Preview state
  const [previewAsset, setPreviewAsset] = useState<AssetDetail | null>(null)

  // Upload state
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Selection state
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isBatchDownloading, setIsBatchDownloading] = useState(false)

  // Computed date filters
  const dateFilters = getDateFilters(timeRange, dateRange)

  // Fetch products for filter dropdown
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  // Fetch assets with filters
  const { data: assets, isLoading } = useQuery({
    queryKey: ['assets', productId, sourceType, assetType, isFavorited, timeRange, dateRange?.from?.toISOString(), dateRange?.to?.toISOString(), offset, embedRequirementId],
    queryFn: () =>
      api.getAssets({
        product_id: productId || undefined,
        source_type: sourceType || undefined,
        asset_type: assetType || undefined,
        is_favorited: isFavorited ?? undefined,
        created_after: dateFilters.created_after,
        created_before: dateFilters.created_before,
        offset,
        limit,
        include_original_urls: false,
        requirement_id: embedRequirementId ?? undefined,
      }),
  })

  // Fetch asset stats via aggregation endpoint
  const { data: stats } = useQuery({
    queryKey: ['assets-stats', productId, embedRequirementId],
    queryFn: () => api.getAssetStats(productId || undefined, embedRequirementId ?? undefined),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      queryClient.invalidateQueries({ queryKey: ['assets-stats'] })
      toast({ title: 'Asset deleted' })
    },
  })

  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: (id: string) => api.toggleFavorite(id),
    onSuccess: (asset) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      queryClient.invalidateQueries({ queryKey: ['assets-stats'] })
      toast({ title: asset.is_favorited ? 'Added to favorites' : 'Removed from favorites' })
    },
  })

  // Upload processing
  const processUploadQueue = async (files: File[]) => {
    setIsUploading(true)

    // Add all files to queue with pending status
    const newItems: UploadItem[] = files.map((file) => ({
      id: Math.random().toString(36).slice(2),
      file,
      status: 'pending' as const,
      progress: 0,
    }))

    setUploadQueue((prev) => [...prev, ...newItems])

    // Process files sequentially
    for (const item of newItems) {
      setUploadQueue((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: 'uploading' as const } : i))
      )

      try {
        const asset = await api.uploadAssetWithProgress(
          item.file,
          undefined,
          undefined,
          (progress) => {
            setUploadQueue((prev) =>
              prev.map((i) => (i.id === item.id ? { ...i, progress } : i))
            )
          },
          embedRequirementId ?? undefined,
        )

        setUploadQueue((prev) =>
          prev.map((i) =>
            i.id === item.id
              ? { ...i, status: 'success' as const, progress: 100, resultAsset: asset }
              : i
          )
        )
      } catch {
        setUploadQueue((prev) =>
          prev.map((i) =>
            i.id === item.id ? { ...i, status: 'error' as const, error: 'Upload failed' } : i
          )
        )
      }
    }

    setIsUploading(false)
    queryClient.invalidateQueries({ queryKey: ['assets'] })
    queryClient.invalidateQueries({ queryKey: ['assets-stats'] })

    // Show success toast
    const successCount = newItems.length
    if (successCount > 0) {
      toast({ title: `${successCount} file${successCount > 1 ? 's' : ''} uploaded` })
    }
  }

  const handleRemoveUploadItem = (id: string) => {
    setUploadQueue((prev) => prev.filter((i) => i.id !== id))
  }

  const handleClearCompleted = () => {
    setUploadQueue((prev) => prev.filter((i) => i.status !== 'success'))
  }

  const handleUploadClick = () => {
    if (!isUploading) {
      uploadInputRef.current?.click()
    }
  }

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validFiles = validateAssetFiles(Array.from(event.target.files || []), 'all')
    event.target.value = ''
    if (validFiles.length === 0) {
      return
    }
    void processUploadQueue(validFiles)
  }

  const clearFilters = () => {
    setProductId('')
    setSourceType('')
    setAssetType('')
    setIsFavorited(null)
    setTimeRange('')
    setDateRange(undefined)
    setOffset(0)
  }

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteAssets(ids),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      queryClient.invalidateQueries({ queryKey: ['assets-stats'] })
      setSelectedIds(new Set())
      setIsSelectMode(false)
      toast({ title: `Deleted ${ids.length} assets` })
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (!assets?.items) return
    setSelectedIds(new Set(assets.items.map((a) => a.id)))
  }

  const deselectAll = () => setSelectedIds(new Set())

  const exitSelectMode = () => {
    setIsSelectMode(false)
    setSelectedIds(new Set())
  }

  const fetchAssetDetail = async (assetId: string) =>
    queryClient.fetchQuery({
      queryKey: ['asset', assetId],
      queryFn: () => api.getAsset(assetId),
      staleTime: 0,
    })

  const handlePreview = async (asset: Asset) => {
    try {
      setPreviewAsset(await fetchAssetDetail(asset.id))
    } catch {
      toast({ title: 'Preview unavailable', variant: 'destructive' })
    }
  }

  const handleDownload = async (asset: Asset) => {
    try {
      const result = await api.recordAssetDownload(asset.id)
      const downloadUrl = result.download_url
      if (!downloadUrl) {
        toast({ title: 'Download unavailable', variant: 'destructive' })
        return
      }
      downloadFile(downloadUrl)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  const handleBatchDownload = async () => {
    if (selectedIds.size === 0 || !assets?.items) return
    setIsBatchDownloading(true)

    try {
      const selected = assets.items.filter((a) => selectedIds.has(a.id))
      const detailedAssets = await Promise.allSettled(
        selected.map(async (asset) => ({
          asset,
          result: await api.recordAssetDownload(asset.id),
        }))
      )
      const files = detailedAssets
        .flatMap((result, i) => {
          if (result.status !== 'fulfilled') {
            return []
          }
          const { asset } = result.value
          const url = result.value.result.download_url
          if (!url) return []
          return [{
            url,
            filename: asset.file_name || `asset-${i + 1}.${asset.asset_type === 'video' ? 'mp4' : 'png'}`,
          }]
        })

      if (files.length === 0) {
        toast({ title: 'No downloadable assets selected', variant: 'destructive' })
        return
      }

      const { succeeded, failed } = await downloadFilesAsZip(
        files,
        `creatives-${new Date().toISOString().slice(0, 10)}.zip`,
      )
      const msg = failed > 0 ? `Downloaded ${succeeded} files (${failed} failed)` : `Downloaded ${succeeded} files`
      toast({ title: msg })
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    } finally {
      setIsBatchDownloading(false)
    }
  }

  const handleBatchDelete = () => {
    if (selectedIds.size === 0) return
    if (confirm(`Delete ${selectedIds.size} selected assets?`)) {
      bulkDeleteMutation.mutate(Array.from(selectedIds))
    }
  }

  const hasFilters = productId || sourceType || assetType || isFavorited !== null || timeRange

  return (
    <>
    <div className="space-y-6">
      <input
        ref={uploadInputRef}
        type="file"
        accept={getAssetUploadAccept('all')}
        multiple
        className="hidden"
        onChange={handleUploadInputChange}
        disabled={isUploading}
      />

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4 space-y-3">
        {/* Row 1: Product, Source Type, Asset Type */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {/* Product Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Product</span>
            <select
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value)
                setOffset(0)
              }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary max-w-[280px]"
            >
              <option value="">All Products</option>
              {products?.items.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>

          <div className="h-6 w-px bg-gray-200" />

          {/* Source Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Source</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setSourceType('')
                  setOffset(0)
                }}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  sourceType === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {VISIBLE_SOURCE_TYPES.map((type) => {
                const config = SOURCE_TYPE_CONFIG[type]
                const Icon = config.icon
                return (
                  <button
                    key={type}
                    onClick={() => {
                      setSourceType(type)
                      setOffset(0)
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                      sourceType === type
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="h-6 w-px bg-gray-200" />

          {/* Asset Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Type</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setAssetType('')
                  setOffset(0)
                }}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  assetType === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {(Object.keys(ASSET_TYPE_CONFIG) as AssetType[]).map((type) => {
                const config = ASSET_TYPE_CONFIG[type]
                const Icon = config.icon
                return (
                  <button
                    key={type}
                    onClick={() => {
                      setAssetType(type)
                      setOffset(0)
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                      assetType === type
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Row 2: Favorites, Time Range, Clear */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {/* Favorites Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Collection</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setIsFavorited(null)
                  setOffset(0)
                }}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  isFavorited === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => {
                  setIsFavorited(true)
                  setOffset(0)
                }}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                  isFavorited === true
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Heart className="h-3 w-3" />
                Favorites
              </button>
            </div>
          </div>

          <div className="h-6 w-px bg-gray-200" />

          {/* Time Range Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Time</span>
            <div className="flex items-center gap-1">
              {TIME_RANGE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => {
                    setTimeRange(value)
                    if (value !== 'custom') setDateRange(undefined)
                    setOffset(0)
                  }}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    timeRange === value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    onClick={() => {
                      setTimeRange('custom')
                      setOffset(0)
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                      timeRange === 'custom'
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <CalendarIcon className="h-3 w-3" />
                    {timeRange === 'custom' && dateRange?.from ? (
                      dateRange.to ? (
                        <>{format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d')}</>
                      ) : (
                        format(dateRange.from, 'MMM d')
                      )
                    ) : (
                      'Custom'
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range)
                      setTimeRange('custom')
                      setOffset(0)
                    }}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <>
              <div className="h-6 w-px bg-gray-200" />
              <button
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                Clear all
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {VISIBLE_SOURCE_TYPES.map((type) => {
          const config = SOURCE_TYPE_CONFIG[type]
          const Icon = config.icon
          const count = stats?.[type] ?? 0
          return (
            <div
              key={type}
              className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border-l-4 ${
                sourceType === type
                  ? 'ring-2 ring-primary border-l-primary'
                  : type === 'screenshot' ? 'border-l-blue-400 border-gray-200/60'
                  : type === 'uploaded' ? 'border-l-green-400 border-gray-200/60'
                  : type === 'generated' ? 'border-l-purple-400 border-gray-200/60'
                  : 'border-l-orange-400 border-gray-200/60'
              }`}
              onClick={() => {
                setSourceType(sourceType === type ? '' : type)
                setOffset(0)
              }}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${config.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500">{config.label}</p>
                </div>
              </div>
            </div>
          )
        })}
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 text-gray-700">
              <FolderOpen className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.total ?? 0}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Zone - Only visible when "Uploaded" filter is active */}
      {sourceType === 'uploaded' && (
        <FileUploadZone onUpload={processUploadQueue} isUploading={isUploading} />
      )}

      {/* Asset Grid */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Assets {assets?.total ? `(${assets.total})` : ''}
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleUploadClick} disabled={isUploading}>
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-1" />
              )}
              Upload
            </Button>
            {isSelectMode ? (
              <>
                <span className="text-sm text-gray-500">
                  {selectedIds.size} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectedIds.size === (assets?.items.length ?? 0) ? deselectAll : selectAll}
                >
                  {selectedIds.size === (assets?.items.length ?? 0) ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.size === 0 || isBatchDownloading}
                  onClick={handleBatchDownload}
                >
                  {isBatchDownloading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.size === 0 || bulkDeleteMutation.isPending}
                  onClick={handleBatchDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={exitSelectMode}>
                  Cancel
                </Button>
              </>
            ) : (
              assets && assets.items.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setIsSelectMode(true)}>
                  Select
                </Button>
              )
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 p-3">
            {Array.from({ length: 14 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-gray-200/80 animate-pulse" />
            ))}
          </div>
        ) : assets?.items.length === 0 ? (
          sourceType === 'uploaded' ? null : (
            <div className="p-16 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <FolderOpen className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No assets found</p>
              <p className="text-sm text-gray-400 mt-1">Upload images or generate creatives to get started</p>
              {hasFilters && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          )
        ) : (
          <>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 p-3">
              {assets?.items.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  isSelectMode={isSelectMode}
                  isSelected={selectedIds.has(asset.id)}
                  onToggleSelect={() => toggleSelect(asset.id)}
                  onPreview={() => void handlePreview(asset)}
                  onDelete={() => {
                    if (confirm('Delete this asset?')) {
                      deleteMutation.mutate(asset.id)
                    }
                  }}
                  onToggleFavorite={() => toggleFavoriteMutation.mutate(asset.id)}
                  onDownload={() => void handleDownload(asset)}
                />
              ))}
            </div>

            {/* Pagination */}
            {assets && assets.total > limit && (
              <div className="p-4 border-t flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Previous
                </Button>
                <PageJumper
                  key={Math.floor(offset / limit) + 1}
                  currentPage={Math.floor(offset / limit) + 1}
                  totalPages={Math.ceil(assets.total / limit)}
                  onJump={(page) => setOffset((page - 1) * limit)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + limit >= assets.total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {/* Preview Modal */}
    {previewAsset && (
      <PreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} onDownload={handleDownload} />
    )}

    {/* Upload Progress Panel */}
    <UploadProgressList
      items={uploadQueue}
      onRemove={handleRemoveUploadItem}
      onClearCompleted={handleClearCompleted}
    />
    </>
  )
}

function PageJumper({
  currentPage,
  totalPages,
  onJump,
}: {
  currentPage: number
  totalPages: number
  onJump: (page: number) => void
}) {
  const [value, setValue] = useState(String(currentPage))

  const commit = () => {
    const page = parseInt(value, 10)
    if (Number.isNaN(page)) {
      setValue(String(currentPage))
      return
    }
    const clamped = Math.min(Math.max(1, page), totalPages)
    setValue(String(clamped))
    if (clamped !== currentPage) onJump(clamped)
  }

  return (
    <span className="text-sm text-gray-500 flex items-center gap-1.5">
      Page
      <Input
        type="number"
        min={1}
        max={totalPages}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className="h-8 w-16 px-2 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      of {totalPages}
    </span>
  )
}

function AssetCard({
  asset,
  isSelectMode,
  isSelected,
  onToggleSelect,
  onPreview,
  onDelete,
  onToggleFavorite,
  onDownload,
}: {
  asset: Asset
  isSelectMode: boolean
  isSelected: boolean
  onToggleSelect: () => void
  onPreview: () => void
  onDelete: () => void
  onToggleFavorite: () => void
  onDownload: () => void
}) {
  const config = SOURCE_TYPE_CONFIG[asset.source_type]
  const Icon = config.icon
  const isVideo = asset.asset_type === 'video'
  const thumbnailUrl = getAssetThumbnailUrl(asset)
  const frameClassName = `aspect-square bg-gray-100 rounded-lg overflow-hidden ${
    isSelected ? 'ring-2 ring-primary ring-offset-1' : ''
  }`

  const handleClick = () => {
    if (isSelectMode) {
      onToggleSelect()
    } else {
      onPreview()
    }
  }

  return (
    <div className="relative group cursor-pointer" onClick={handleClick}>
      {/* Thumbnail */}
      <div className={frameClassName}>
        {thumbnailUrl ? (
          isVideo ? (
            <div className="relative w-full h-full">
              <img
                src={thumbnailUrl}
                alt={asset.description || 'Asset'}
                className="w-full h-full object-contain bg-gray-50"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                  <Video className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          ) : (
            <img
              src={thumbnailUrl}
              alt={asset.description || 'Asset'}
              className="w-full h-full object-contain bg-gray-50"
              loading="lazy"
              decoding="async"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            {isVideo ? (
              <div className="flex flex-col items-center gap-2 text-gray-400">
                <Video className="h-8 w-8" />
                <span className="text-[10px] font-medium uppercase tracking-wide">No thumbnail</span>
              </div>
            ) : (
              <Image className="h-8 w-8 text-gray-300" />
            )}
          </div>
        )}
      </div>

      {/* Select Mode Checkbox */}
      {isSelectMode ? (
        <div className="absolute top-1.5 left-1.5 z-20">
          {isSelected ? (
            <CheckSquare className="h-5 w-5 text-primary fill-primary/10" />
          ) : (
            <Square className="h-5 w-5 text-gray-400 bg-white/80 rounded" />
          )}
        </div>
      ) : (
        /* Source Type Badge - z-20 to stay above hover overlay */
        <div className={`absolute top-1.5 left-1.5 z-20 px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-0.5 ${config.color}`}>
          <Icon className="h-2.5 w-2.5" />
          {config.label}
        </div>
      )}

      {/* Favorite Button - z-20 to stay above hover overlay */}
      {!isSelectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggleFavorite()
          }}
          className="absolute top-1.5 right-1.5 z-20 p-1 rounded-full bg-white/80 hover:bg-white transition-colors"
        >
          <Heart
            className={`h-3.5 w-3.5 ${
              asset.is_favorited
                ? 'fill-red-500 text-red-500'
                : 'text-gray-400 hover:text-red-400'
            }`}
          />
        </button>
      )}

      {/* Score Badge - z-20 to stay above hover overlay */}
      {asset.score && !isSelectMode && (
        <div className="absolute top-8 right-1.5 z-20 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded">
          {asset.score.toFixed(1)}
        </div>
      )}

      {/* Hover Actions - z-10 so favorite button (z-20) stays clickable */}
      {!isSelectMode && (
        <div className="absolute inset-0 z-10 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="p-1.5 bg-white rounded-full hover:bg-gray-100 transition-colors"
          >
            <Maximize2 className="h-3.5 w-3.5 text-gray-700" />
          </button>
          <button
            className="p-1.5 bg-white rounded-full hover:bg-gray-100 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onDownload()
            }}
          >
            <Download className="h-3.5 w-3.5 text-gray-700" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 bg-white rounded-full hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5 text-red-600" />
          </button>
        </div>
      )}

      {/* Date */}
      <p className="mt-1 text-xs text-gray-500">{formatDate(asset.created_at)}</p>
    </div>
  )
}

function GenerationDetailsSection({ details }: { details: GenerationDetails }) {
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: 'Copied prompt' })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Generation Details</h3>
        <p className="mt-1 text-xs text-gray-500">
          Saved from the generation request for reuse and reference.
        </p>
      </div>

      {details.text_inputs.length > 0 ? (
        <div className="space-y-3">
          {details.text_inputs.map((input) => (
            <div key={input.key} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-900">{input.label}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleCopy(input.text)}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
              <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-sm leading-6 text-gray-700">
                {input.text}
              </pre>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          No manual input was saved for this asset.
        </div>
      )}

      {details.fields.length > 0 && (
        <div className="rounded-xl border border-gray-200">
          <dl className="divide-y divide-gray-200">
            {details.fields.map((field) => (
              <div key={field.key} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
                <dt className="text-gray-500">{field.label}</dt>
                <dd className="max-w-[65%] break-words text-right font-medium text-gray-900">
                  {field.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}

// Preview Modal
function PreviewModal({
  asset,
  onClose,
  onDownload,
}: {
  asset: AssetDetail
  onClose: () => void
  onDownload: (asset: AssetDetail) => void
}) {
  const isVideo = asset.asset_type === 'video'
  const downloadUrl = asset.download_url || asset.url
  const generationDetails = asset.source_type === 'generated' ? asset.generation_details : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              {asset.file_name || `${isVideo ? 'Video' : 'Image'} Preview`}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {isVideo ? 'Preview and generation details' : 'Asset preview and generation details'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {downloadUrl && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDownload(asset)}
              >
                <Download className="mr-1.5 h-4 w-4" />
                Download
              </Button>
            )}
            <Button type="button" variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="flex min-h-[320px] flex-1 items-center justify-center bg-black px-6 py-8 lg:min-h-0">
            {asset.url ? (
              isVideo ? (
                <video
                  src={asset.url}
                  className="max-h-[60vh] max-w-full rounded-xl"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={asset.url}
                  alt={asset.description || 'Asset'}
                  className="max-h-[60vh] max-w-full rounded-xl object-contain"
                />
              )
            ) : (
              <div className="flex h-64 w-64 items-center justify-center rounded-xl bg-gray-900">
                <Image className="h-16 w-16 text-gray-600" />
              </div>
            )}
          </div>

          {generationDetails && (
            <div className="max-h-[42vh] overflow-y-auto border-t border-gray-200 bg-white p-6 lg:max-h-none lg:w-[380px] lg:border-l lg:border-t-0">
              <GenerationDetailsSection details={generationDetails} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
